import React, { useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { Calendar, Clock, ArrowLeft, Share2, BookOpen, CheckCircle, AlertCircle, DollarSign, Shield, Star } from 'lucide-react';
import { brandTheme } from '../../styles/BrandTheme';
import { trackBlogView } from '../../utils/gtag';
import HeroSectionComponent from '../../components/HeroSection';

const ArticleContainer = styled.div`
  max-width: 800px;
  margin: 0 auto;
  padding: 2rem 1rem;

  /* iPad Pro and Large Tablets */
  @media (max-width: 1366px) and (min-width: 1025px) {
    padding: 2rem 1.5rem;
  }

  /* iPad Air and Medium Tablets */
  @media (max-width: 1024px) and (min-width: 769px) {
    padding: 1.75rem 1.25rem;
  }

  /* Landscape Mobile and Small Tablets */
  @media (max-width: 768px) and (min-width: 569px) and (orientation: landscape) {
    padding: 1.25rem 1rem;
  }

  /* Portrait Mobile - iPhone 12 Pro Max and Large Mobile */
  @media (max-width: 768px) and (min-width: 428px) and (orientation: portrait) {
    padding: 1.5rem 1rem;
  }

  /* iPhone 12 and Standard Mobile */
  @media (max-width: 427px) and (min-width: 390px) {
    padding: 1.25rem 0.875rem;
  }

  /* iPhone SE and Small Mobile */
  @media (max-width: 389px) and (min-width: 320px) {
    padding: 1rem 0.75rem;
  }

  /* Landscape orientation adjustments */
  @media (orientation: landscape) and (max-height: 500px) {
    padding: 0.75rem 0.5rem;
  }
`;

const BackLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  color: ${brandTheme.colors.primary[600]};
  text-decoration: none;
  margin-bottom: 2rem;
  font-weight: 500;
  transition: all 0.3s ease;

  &:hover {
    color: ${brandTheme.colors.primary[700]};
    gap: 0.75rem;
  }

  svg {
    transition: transform 0.3s ease;
  }

  &:hover svg {
    transform: translateX(-4px);
  }
`;

const ArticleHeader = styled.div`
  margin-bottom: 3rem;

  .category {
    display: inline-block;
    background: ${brandTheme.colors.primary[100]};
    color: ${brandTheme.colors.primary[700]};
    padding: 0.5rem 1rem;
    border-radius: ${brandTheme.borderRadius.full};
    font-size: 0.9rem;
    font-weight: 600;
    margin-bottom: 1rem;
  }

  h1 {
    font-size: clamp(2rem, 4vw, 3rem);
    line-height: 1.2;
    margin-bottom: 1.5rem;
    color: ${brandTheme.colors.secondary[900]};

    /* iPhone SE and Small Mobile */
    @media (max-width: 389px) and (min-width: 320px) {
      font-size: clamp(1.5rem, 6vw, 2.25rem);
      line-height: 1.1;
      margin-bottom: 1rem;
    }

    /* Landscape orientation adjustments */
    @media (orientation: landscape) and (max-height: 500px) {
      font-size: clamp(1.25rem, 4vw, 1.75rem);
      margin-bottom: 0.75rem;
    }
  }

  .excerpt {
    font-size: 1.25rem;
    color: ${brandTheme.colors.secondary[600]};
    line-height: 1.6;
    margin-bottom: 2rem;
  }

  .meta {
    display: flex;
    gap: 2rem;
    align-items: center;
    color: ${brandTheme.colors.neutral.gray[500]};
    font-size: 0.95rem;
    flex-wrap: wrap;

    svg {
      width: 18px;
      height: 18px;
      margin-right: 0.5rem;
    }

    /* iPhone SE and Small Mobile */
    @media (max-width: 389px) and (min-width: 320px) {
      gap: 0.75rem;
      font-size: 0.85rem;
    }

    /* Landscape orientation adjustments */
    @media (orientation: landscape) and (max-height: 500px) {
      gap: 0.5rem;
      font-size: 0.8rem;
    }
  }
`;

const ArticleImage = styled.div`
  height: 400px;
  background: linear-gradient(135deg, ${brandTheme.colors.primary[100]}, ${brandTheme.colors.primary[200]});
  border-radius: ${brandTheme.borderRadius.xl};
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 3rem;
  position: relative;
  overflow: hidden;

  /* iPad Air and Medium Tablets */
  @media (max-width: 1024px) and (min-width: 769px) {
    height: 350px;
    margin-bottom: 2.5rem;
  }

  /* Landscape Mobile and Small Tablets */
  @media (max-width: 768px) and (min-width: 569px) and (orientation: landscape) {
    height: 250px;
    margin-bottom: 2rem;
  }

  /* Portrait Mobile - iPhone 12 Pro Max and Large Mobile */
  @media (max-width: 768px) and (min-width: 428px) and (orientation: portrait) {
    height: 300px;
    margin-bottom: 2.5rem;
  }

  /* iPhone 12 and Standard Mobile */
  @media (max-width: 427px) and (min-width: 390px) {
    height: 250px;
    margin-bottom: 2rem;
  }

  /* iPhone SE and Small Mobile */
  @media (max-width: 389px) and (min-width: 320px) {
    height: 200px;
    margin-bottom: 1.5rem;
  }

  /* Landscape orientation adjustments */
  @media (orientation: landscape) and (max-height: 500px) {
    height: 150px;
    margin-bottom: 1rem;
  }

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: url('data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"><circle cx="50" cy="50" r="2" fill="rgba(59,130,246,0.1)"/></svg>');
  }

  .placeholder {
    font-size: 4rem;
    color: ${brandTheme.colors.primary[300]};
  }
`;

const ArticleContent = styled.div`
  font-size: 1.1rem;
  line-height: 1.8;
  color: ${brandTheme.colors.secondary[700]};

  h2 {
    font-size: 1.75rem;
    margin: 3rem 0 1.5rem 0;
    color: ${brandTheme.colors.secondary[900]};
    position: relative;

    &::before {
      content: '';
      position: absolute;
      left: -1.5rem;
      top: 50%;
      transform: translateY(-50%);
      width: 4px;
      height: 24px;
      background: linear-gradient(180deg, ${brandTheme.colors.primary[500]}, ${brandTheme.colors.primary[700]});
      border-radius: 2px;

      @media (max-width: 768px) {
        left: -1rem;
      }
    }

    @media (max-width: 768px) {
      font-size: 1.5rem;
      margin-left: 1rem;
    }
  }

  h3 {
    font-size: 1.4rem;
    margin: 2.5rem 0 1rem 0;
    color: ${brandTheme.colors.secondary[900]};
  }

  p {
    margin-bottom: 1.5rem;
  }

  ul, ol {
    margin: 1.5rem 0;
    padding-left: 2rem;

    li {
      margin-bottom: 0.75rem;
    }
  }

  blockquote {
    background: ${brandTheme.colors.primary[50]};
    border-left: 4px solid ${brandTheme.colors.primary[500]};
    padding: 1.5rem;
    margin: 2rem 0;
    border-radius: 0 ${brandTheme.borderRadius.lg} ${brandTheme.borderRadius.lg} 0;
    font-style: italic;
    color: ${brandTheme.colors.secondary[700]};
  }
`;

const InfoBox = styled.div<{ type?: 'warning' | 'tip' | 'success' }>`
  background: ${props => props.type === 'warning' ? '#fef3c7' : props.type === 'tip' ? '#dbeafe' : '#f0fdf4'};
  border: 1px solid ${props => props.type === 'warning' ? '#fbbf24' : props.type === 'tip' ? '#FECE1B' : '#22c55e'};
  border-radius: ${brandTheme.borderRadius.lg};
  padding: 1.5rem;
  margin: 2rem 0;
  display: flex;
  gap: 1rem;
  align-items: flex-start;

  svg {
    color: ${props => props.type === 'warning' ? '#f59e0b' : props.type === 'tip' ? '#FECE1B' : '#16a34a'};
    flex-shrink: 0;
    margin-top: 0.25rem;
  }

  div {
    h4 {
      margin: 0 0 0.5rem 0;
      color: ${props => props.type === 'warning' ? '#92400e' : props.type === 'tip' ? '#d97706' : '#14532d'};
      font-size: 1rem;
      font-weight: 600;
    }

    p {
      margin: 0;
      color: ${props => props.type === 'warning' ? '#92400e' : props.type === 'tip' ? '#d97706' : '#14532d'};
    }
  }
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  margin: 2rem 0;
  background: white;
  border-radius: ${brandTheme.borderRadius.lg};
  overflow: hidden;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.05);

  th, td {
    padding: 1rem;
    text-align: left;
    border-bottom: 1px solid ${brandTheme.colors.neutral.gray[200]};
  }

  th {
    background: ${brandTheme.colors.primary[50]};
    font-weight: 600;
    color: ${brandTheme.colors.secondary[900]};
  }

  tr:last-child td {
    border-bottom: none;
  }
`;

const CTA = styled.div`
  background: linear-gradient(135deg, ${brandTheme.colors.primary[600]}, ${brandTheme.colors.primary[800]});
  border-radius: ${brandTheme.borderRadius.xl};
  padding: 2rem;
  text-align: center;
  color: white;
  margin: 3rem 0;

  h3 {
    color: white;
    margin-bottom: 1rem;
  }

  p {
    margin-bottom: 1.5rem;
    opacity: 0.9;
  }

  .cta-buttons {
    display: flex;
    gap: 1rem;
    justify-content: center;
    flex-wrap: wrap;

    a {
      display: inline-flex;
      align-items: center;
      gap: 0.5rem;
      padding: 0.75rem 2rem;
      border-radius: ${brandTheme.borderRadius.lg};
      text-decoration: none;
      font-weight: 600;
      transition: all 0.3s ease;

      &.primary {
        background: white;
        color: ${brandTheme.colors.primary[600]};

        &:hover {
          background: ${brandTheme.colors.primary[50]};
          transform: translateY(-2px);
        }
      }

      &.secondary {
        background: rgba(255, 255, 255, 0.2);
        color: white;
        border: 2px solid rgba(255, 255, 255, 0.3);

        &:hover {
          background: rgba(255, 255, 255, 0.3);
        }
      }
    }
  }
`;

const UltimateGuideResinBoundPage: React.FC = () => {
  useEffect(() => {
    trackBlogView('The Ultimate Guide to Resin Bound Surfacing');
  }, []);

  return (
    <>
      <Helmet>
        <title>The Ultimate Guide to Resin Bound Surfacing: Everything You Need to Know | ZAN RESIN SURFACES LTD</title>
        <meta name="description" content="Complete guide to resin bound surfacing covering installation, benefits, costs, maintenance, and design options. Expert advice from London's leading specialists." />
        <meta name="keywords" content="resin bound surfacing guide, resin driveway installation, resin bound benefits, resin surfacing cost, resin bound maintenance, London resin specialists" />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href="https://www.resinsurfaceslondon.co.uk/blog/ultimate-guide-resin-bound-surfacing/" />
        
        {/* Open Graph */}
        <meta property="og:title" content="The Ultimate Guide to Resin Bound Surfacing: Everything You Need to Know" />
        <meta property="og:description" content="Complete guide to resin bound surfacing covering installation, benefits, costs, maintenance, and design options." />
        <meta property="og:type" content="article" />
        <meta property="og:url" content="https://www.resinsurfaceslondon.co.uk/blog/ultimate-guide-resin-bound-surfacing/" />
        <meta property="og:image" content="https://www.resinsurfaceslondon.co.uk/images/resin-surfaces-bound-work1.jpeg" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:image:alt" content="Resin bound surfacing installation guide" />
        <meta property="og:site_name" content="ZAN RESIN SURFACES LTD" />
        <meta property="article:published_time" content="2025-01-15T00:00:00+00:00" />
        <meta property="article:modified_time" content="2025-01-15T00:00:00+00:00" />
        <meta property="article:author" content="ZAN RESIN SURFACES LTD" />
        <meta property="article:section" content="Installation Guide" />
        <meta property="article:tag" content="resin bound surfacing" />
        <meta property="article:tag" content="resin driveway installation" />
        <meta property="article:tag" content="resin bound benefits" />
        
        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="The Ultimate Guide to Resin Bound Surfacing: Everything You Need to Know" />
        <meta name="twitter:description" content="Complete guide to resin bound surfacing covering installation, benefits, costs, maintenance, and design options." />
        <meta name="twitter:image" content="https://www.resinsurfaceslondon.co.uk/images/resin-surfaces-bound-work1.jpeg" />
        <meta name="twitter:image:alt" content="Resin bound surfacing installation guide" />
        <meta name="twitter:site" content="@resinsurfaceslondon" />
        <meta name="twitter:creator" content="@resinsurfaceslondon" />
        
        {/* Additional SEO Meta Tags */}
        <meta name="geo.region" content="GB-LND" />
        <meta name="geo.placename" content="London" />
        <meta name="geo.position" content="51.5074;-0.1278" />
        <meta name="ICBM" content="51.5074, -0.1278" />
        <meta name="language" content="en-GB" />
        <meta name="revisit-after" content="7 days" />
        <meta name="distribution" content="global" />
        <meta name="rating" content="general" />
        <meta name="referrer" content="no-referrer-when-downgrade" />
        
        {/* Structured Data */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BlogPosting",
            "headline": "The Ultimate Guide to Resin Bound Surfacing: Everything You Need to Know",
            "description": "Complete guide to resin bound surfacing covering installation, benefits, costs, maintenance, and design options. Expert advice from London's leading specialists.",
            "url": "https://www.resinsurfaceslondon.co.uk/blog/ultimate-guide-resin-bound-surfacing/",
            "datePublished": "2025-01-15T00:00:00+00:00",
            "dateModified": "2025-01-15T00:00:00+00:00",
            "author": {
              "@type": "Organization",
              "name": "ZAN RESIN SURFACES LTD",
              "url": "https://www.resinsurfaceslondon.co.uk",
              "logo": {
                "@type": "ImageObject",
                "url": "https://www.resinsurfaceslondon.co.uk/images/resin-surfaces-bound-work1.jpeg"
              },
              "address": {
                "@type": "PostalAddress",
                "addressLocality": "London",
                "addressCountry": "GB"
              }
            },
            "publisher": {
              "@type": "Organization",
              "name": "ZAN RESIN SURFACES LTD",
              "url": "https://www.resinsurfaceslondon.co.uk",
              "logo": {
                "@type": "ImageObject",
                "url": "https://www.resinsurfaceslondon.co.uk/images/resin-surfaces-bound-work1.jpeg"
              }
            },
            "mainEntityOfPage": {
              "@type": "WebPage",
              "@id": "https://www.resinsurfaceslondon.co.uk/blog/ultimate-guide-resin-bound-surfacing/"
            },
            "image": {
              "@type": "ImageObject",
              "url": "https://www.resinsurfaceslondon.co.uk/images/resin-surfaces-bound-work1.jpeg",
              "width": 1200,
              "height": 630
            },
            "wordCount": 3500,
            "timeRequired": "PT12M",
            "articleSection": "Installation Guide",
            "keywords": "resin bound surfacing, resin driveway installation, resin bound benefits, resin surfacing cost, resin bound maintenance, London resin specialists",
            "inLanguage": "en-GB",
            "isAccessibleForFree": true,
            "speakable": {
              "@type": "SpeakableSpecification",
              "cssSelector": "h1, h2, h3, .excerpt"
            },
            "about": [
              {
                "@type": "Thing",
                "name": "Resin Bound Surfacing"
              },
              {
                "@type": "Thing", 
                "name": "Driveway Installation"
              },
              {
                "@type": "Thing",
                "name": "London Construction"
              }
            ]
          })}
        </script>
      </Helmet>

      <HeroSectionComponent
        title="The Ultimate Guide to Resin Bound Surfacing"
        subtitle="Everything you need to know about resin bound surfacing - from installation and benefits to costs and maintenance. Expert advice from London's leading specialists."
        badge="Complete Guide • Expert Tips • Professional Advice"
        backgroundImage="/images/resin-surfaces-bound-work1.jpeg"
        primaryButtonText="Get Free Quote"
        primaryButtonLink="/contact/"
        secondaryButtonText="Call Now"
        secondaryButtonLink="tel:+447535935779"
      />

      <ArticleContainer>
        <BackLink to="/blog/">
          <ArrowLeft size={18} />
          Back to Blog
        </BackLink>

        <ArticleHeader>
          <span className="category">Installation Guide</span>
          <h1>The Ultimate Guide to Resin Bound Surfacing: Everything You Need to Know</h1>
          <p className="excerpt">
            Discover everything about resin bound surfacing - from installation process and benefits to maintenance tips and cost considerations. Your complete guide to transforming outdoor spaces.
          </p>
          <div className="meta">
            <span>
              <Calendar size={18} />
              January 15, 2025
            </span>
            <span>
              <Clock size={18} />
              12 min read
            </span>
            <span>
              <BookOpen size={18} />
              3,500 words
            </span>
          </div>
        </ArticleHeader>

        <ArticleImage>
          <img 
            src="/images/resin-surfaces-bound-work1.jpeg" 
            alt="Resin bound surfacing installation guide showing professional installation process"
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              borderRadius: 'inherit'
            }}
          />
        </ArticleImage>

        <ArticleContent>
          <p>
            Resin bound surfacing has revolutionized outdoor spaces across the UK, offering a perfect blend of aesthetics, durability, and functionality. Whether you're considering a new driveway, pathway, or pool deck, this comprehensive guide will provide you with everything you need to know about resin bound surfacing.
          </p>

          <h2>What is Resin Bound Surfacing?</h2>
          
          <p>
            Resin bound surfacing is a modern paving solution that combines natural aggregates with a clear polyurethane resin binder. This creates a seamless, porous surface that's both visually appealing and highly functional. Unlike traditional paving methods, resin bound surfaces offer superior drainage, durability, and design flexibility.
          </p>

          <InfoBox type="tip">
            <CheckCircle size={20} />
            <div>
              <h4>Pro Tip</h4>
              <p>Resin bound surfacing is permeable, making it an excellent choice for areas requiring SuDS (Sustainable Drainage Systems) compliance.</p>
            </div>
          </InfoBox>

          <h2>Key Benefits of Resin Bound Surfacing</h2>

          <h3>1. Superior Drainage</h3>
          <p>
            One of the most significant advantages of resin bound surfacing is its permeability. The surface allows water to drain through naturally, reducing flooding risks and meeting planning requirements for permeable surfaces.
          </p>

          <h3>2. Durability and Longevity</h3>
          <p>
            With proper installation and maintenance, resin bound surfaces can last 15-25 years. The combination of high-quality aggregates and UV-stable resin creates a surface that withstands heavy traffic and harsh weather conditions.
          </p>

          <h3>3. Design Flexibility</h3>
          <p>
            Choose from hundreds of aggregate combinations, colors, and finishes to create a unique look that complements your property's aesthetic. From natural stone effects to contemporary designs, the possibilities are endless.
          </p>

          <h3>4. Low Maintenance</h3>
          <p>
            Unlike block paving or concrete, resin bound surfaces don't require regular re-pointing or joint maintenance. Simple cleaning and occasional power washing keep them looking pristine.
          </p>

          <h3>5. Safety and Accessibility</h3>
          <p>
            The seamless surface eliminates trip hazards from loose paving stones, making it ideal for families with children and elderly residents. It's also wheelchair accessible when properly installed.
          </p>

          <h2>Installation Process</h2>

          <p>
            Understanding the installation process helps you make informed decisions and ensures you choose the right contractor for your project.
          </p>

          <h3>1. Site Assessment and Preparation</h3>
          <ul>
            <li>Thorough site survey and measurement</li>
            <li>Assessment of existing surface condition</li>
            <li>Drainage evaluation and planning</li>
            <li>Access route planning for materials and equipment</li>
          </ul>

          <h3>2. Ground Preparation</h3>
          <ul>
            <li>Excavation to appropriate depth (typically 100-150mm)</li>
            <li>Installation of sub-base material (Type 1 MOT or similar)</li>
            <li>Compaction and leveling of sub-base</li>
            <li>Installation of drainage if required</li>
          </ul>

          <h3>3. Edge Installation</h3>
          <ul>
            <li>Installation of edging restraints (kerbs, sleepers, or steel edging)</li>
            <li>Ensuring proper drainage channels</li>
            <li>Creating expansion joints where necessary</li>
          </ul>

          <h3>4. Resin Bound Application</h3>
          <ul>
            <li>Mixing aggregates with resin binder</li>
            <li>Application using specialized equipment</li>
            <li>Immediate troweling to achieve smooth finish</li>
            <li>Quality control and consistency checks</li>
          </ul>

          <h3>5. Curing and Protection</h3>
          <ul>
            <li>Protection from traffic during curing period</li>
            <li>Monitoring of weather conditions</li>
            <li>Final inspection and quality assurance</li>
          </ul>

          <InfoBox type="warning">
            <AlertCircle size={20} />
            <div>
              <h4>Important Note</h4>
              <p>Resin bound surfacing requires specific weather conditions for installation. Most contractors avoid installation during rain, extreme cold, or high humidity.</p>
            </div>
          </InfoBox>

          <h2>Cost Considerations</h2>

          <p>
            Understanding the costs involved helps you budget effectively and compare quotes accurately.
          </p>

          <Table>
            <thead>
              <tr>
                <th>Project Type</th>
                <th>Size (m²)</th>
                <th>Cost Range</th>
                <th>Factors Affecting Price</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>Residential Driveway</td>
                <td>50-100</td>
                <td>£45-£85/m²</td>
                <td>Access, existing surface, drainage</td>
              </tr>
              <tr>
                <td>Pathway/Garden Path</td>
                <td>10-30</td>
                <td>£50-£90/m²</td>
                <td>Complexity, edging requirements</td>
              </tr>
              <tr>
                <td>Pool Deck</td>
                <td>20-50</td>
                <td>£55-£95/m²</td>
                <td>Safety requirements, slip resistance</td>
              </tr>
              <tr>
                <td>Commercial Project</td>
                <td>100+</td>
                <td>£40-£75/m²</td>
                <td>Volume discounts, project complexity</td>
              </tr>
            </tbody>
          </Table>

          <h3>Factors Affecting Cost</h3>

          <ul>
            <li><strong>Site Access:</strong> Difficult access increases labor costs</li>
            <li><strong>Existing Surface:</strong> Removal and disposal costs vary</li>
            <li><strong>Drainage Requirements:</strong> Additional drainage work adds cost</li>
            <li><strong>Aggregate Choice:</strong> Premium aggregates cost more</li>
            <li><strong>Project Size:</strong> Larger projects typically cost less per m²</li>
            <li><strong>Location:</strong> London and surrounding areas may have higher rates</li>
          </ul>

          <h2>Maintenance and Care</h2>

          <p>
            Proper maintenance ensures your resin bound surface retains its appearance and functionality for years to come.
          </p>

          <h3>Regular Maintenance</h3>
          <ul>
            <li><strong>Weekly:</strong> Sweep to remove debris and leaves</li>
            <li><strong>Monthly:</strong> Power wash with mild detergent</li>
            <li><strong>Seasonally:</strong> Deep clean and inspection</li>
            <li><strong>Annually:</strong> Professional maintenance check</li>
          </ul>

          <h3>Stain Removal</h3>
          <ul>
            <li>Oil stains: Use specialist degreasers</li>
            <li>Organic stains: Power wash with detergent</li>
            <li>Paint spills: Remove immediately with appropriate solvents</li>
            <li>Rust stains: Use rust removal products</li>
          </ul>

          <h3>Preventive Measures</h3>
          <ul>
            <li>Place mats at entrances to reduce dirt ingress</li>
            <li>Avoid using harsh chemicals or salt in winter</li>
            <li>Keep drainage channels clear</li>
            <li>Address any damage promptly</li>
          </ul>

          <h2>Common Applications</h2>

          <h3>Driveways</h3>
          <p>
            Resin bound driveways offer excellent durability for vehicle traffic while maintaining an attractive appearance. The permeable nature helps with planning permission requirements in many areas.
          </p>

          <h3>Pathways and Walkways</h3>
          <p>
            Perfect for garden paths, walkways, and pedestrian areas. The seamless surface eliminates trip hazards and provides excellent grip in wet conditions.
          </p>

          <h3>Pool Decks</h3>
          <p>
            Ideal for pool areas due to excellent drainage, slip resistance, and comfort underfoot. The surface stays cool in summer and provides good grip when wet.
          </p>

          <h3>Commercial Applications</h3>
          <p>
            Increasingly popular for car parks, pedestrian areas, and public spaces due to low maintenance requirements and excellent durability.
          </p>

          <h2>Choosing the Right Contractor</h2>

          <p>
            Selecting the right contractor is crucial for a successful resin bound surfacing project.
          </p>

          <h3>What to Look For</h3>
          <ul>
            <li><strong>Experience:</strong> Minimum 5 years in resin bound surfacing</li>
            <li><strong>Insurance:</strong> Public liability and professional indemnity</li>
            <li><strong>Warranties:</strong> Minimum 10-year warranty on workmanship</li>
            <li><strong>References:</strong> Recent projects and customer testimonials</li>
            <li><strong>Certifications:</strong> Industry qualifications and memberships</li>
          </ul>

          <h3>Questions to Ask</h3>
          <ul>
            <li>How long have you been installing resin bound surfaces?</li>
            <li>What warranty do you provide on materials and workmanship?</li>
            <li>Can you provide references from recent projects?</li>
            <li>What happens if there are issues after installation?</li>
            <li>Do you handle all aspects of the project including drainage?</li>
          </ul>

          <InfoBox type="tip">
            <Shield size={20} />
            <div>
              <h4>Quality Assurance</h4>
              <p>Always request a detailed written quote that includes all aspects of the project, warranties, and terms and conditions.</p>
            </div>
          </InfoBox>

          <h2>Environmental Benefits</h2>

          <p>
            Resin bound surfacing offers several environmental advantages over traditional paving methods.
          </p>

          <ul>
            <li><strong>Permeability:</strong> Reduces surface water runoff and flooding</li>
            <li><strong>Natural Materials:</strong> Uses natural aggregates, not synthetic materials</li>
            <li><strong>Longevity:</strong> Reduces need for replacement and waste</li>
            <li><strong>Local Sourcing:</strong> Many aggregates can be sourced locally</li>
            <li><strong>Recyclability:</strong> Can be recycled at end of life</li>
          </ul>

          <h2>Planning Permission Considerations</h2>

          <p>
            In many cases, resin bound surfacing may not require planning permission, but it's essential to check local requirements.
          </p>

          <h3>When Permission May Be Required</h3>
          <ul>
            <li>Properties in conservation areas</li>
            <li>Listed buildings</li>
            <li>Properties with restrictive covenants</li>
            <li>Changes to drainage systems</li>
            <li>Commercial applications</li>
          </ul>

          <h3>Permitted Development Rights</h3>
          <p>
            Most residential driveways fall under permitted development, but check with your local planning authority if unsure.
          </p>

          <CTA>
            <h3>Ready to Transform Your Outdoor Space?</h3>
            <p>
              Get a free, no-obligation quote for your resin bound surfacing project. Our experts are ready to help you create the perfect outdoor space.
            </p>
            <div className="cta-buttons">
              <Link to="/contact/" className="primary">
                <DollarSign size={18} />
                Get Free Quote
              </Link>
              <Link to="/gallery/" className="secondary">
                <Star size={18} />
                View Our Work
              </Link>
            </div>
          </CTA>

          <h2>Conclusion</h2>

          <p>
            Resin bound surfacing represents an excellent investment for any property owner looking to enhance their outdoor space. With its combination of aesthetic appeal, durability, and environmental benefits, it's no wonder this surfacing solution has become increasingly popular across the UK.
          </p>

          <p>
            By understanding the installation process, costs, and maintenance requirements, you can make an informed decision about whether resin bound surfacing is right for your project. Remember to choose an experienced, reputable contractor who can provide the quality and warranty you need for long-term satisfaction.
          </p>

          <p>
            Whether you're planning a new driveway, pathway, or pool deck, resin bound surfacing offers a modern, practical solution that will enhance your property for years to come.
          </p>
        </ArticleContent>
      </ArticleContainer>
    </>
  );
};

export default UltimateGuideResinBoundPage;
